import React, { useState, useEffect, useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback, Button } from 'reactstrap';
import { fetchTableData } from '../../../../Api/ApiMethods';
import { shouldToastError } from '../../../../Api/utils';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { parseCurrencyToFloat, displayDecimal } from '../../../../_helper/regexHandler';
import { useModalCrudState } from '../../../../_helper/hooks/useModalCrudState';
import FilesComponent from '../../CallForms/Component/FilesComponent';
import { PAYMENT_METHODS } from '../../../../_helper/schemaAllowedValues';

const toLocalISOString = (date) => {
    if (!date) return '';
    try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) {
            return '';
        }
        const tzOffset = dateObj.getTimezoneOffset() * 60000;
        const localDate = new Date(dateObj.getTime() - tzOffset);
        return localDate.toISOString().slice(0, 16);
    } catch {
        return '';
    }
};

const PaymentCollectionDataModal = ({ apiEndpoint, isOpen, toggle, mode, selectedPaymentCollection, refreshData, customerId, autoAssignCustomer = false, onSaveSuccess, directExpenseAssociationId, accidentFile, callForm, enforcement }) => {
    const [banks, setBanks] = useState([]);
    const [, setExpenseSearchNumber] = useState('');
    const [associatedExpenseDisplay, setAssociatedExpenseDisplay] = useState('');
    const [associatedExpenseId, setAssociatedExpenseId] = useState(null);
    const [associatedExpenseAmount, setAssociatedExpenseAmount] = useState(null);
    const [, setIsSearchingExpense] = useState(false);

    const extractIdValue = (value) => {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        if (typeof value === 'object') {
            if (value.id !== undefined && value.id !== null && value.id !== '') {
                return String(value.id);
            }
            if (value._id !== undefined && value._id !== null && value._id !== '') {
                return String(value._id);
            }
            if (value.value !== undefined && value.value !== null && value.value !== '') {
                return String(value.value);
            }
            return null;
        }

        const asString = String(value).trim();
        return asString === '' ? null : asString;
    };

    const sanitizeIds = (values) => {
        if (!values) return [];

        if (Array.isArray(values)) {
            return values.map(extractIdValue).filter(Boolean);
        }

        const extracted = extractIdValue(values);
        return extracted ? [extracted] : [];
    };

    const initialPaymentData = useMemo(() => {
        const defaultAccidentFiles = sanitizeIds(accidentFile);
        const defaultCallForms = sanitizeIds(callForm);
        const defaultEnforcements = sanitizeIds(enforcement);
        const defaultExpenseIds = directExpenseAssociationId
            ? sanitizeIds([directExpenseAssociationId])
            : sanitizeIds(associatedExpenseId ? [associatedExpenseId] : []);
        const defaultCustomers = autoAssignCustomer && customerId ? sanitizeIds([customerId]) : [];

        return {
            paymentCollectionNumber: '',
            paymentCollectionType: '',
            amount: '',
            associatedBank: null,
            date: '',
            paymentMethod: '',
            receiptNumber: '',
            description: '',
            expense: defaultExpenseIds,
            accidentFile: defaultAccidentFiles,
            customer: defaultCustomers,
            callForm: defaultCallForms,
            enforcement: defaultEnforcements,
        };
    }, [accidentFile, callForm, enforcement, directExpenseAssociationId, associatedExpenseId, autoAssignCustomer, customerId]);

    const validationSchema = useMemo(() => ({
        paymentCollectionType: {
            required: true,
            message: 'Tahsilat tipi zorunludur.',
            minLength: 2,
            minLengthMessage: 'Tahsilat tipi en az 2 karakter olmalıdır.',
        },
        amount: {
            required: true,
            message: 'Tutar zorunludur.',
            custom: (value) => {
                const numericValue = parseCurrencyToFloat(value);
                if (numericValue <= 0) return 'Geçerli pozitif bir tutar giriniz.';
                return null;
            },
        },
        date: {
            required: true,
            message: 'Tarih zorunludur.',
        },
        associatedBank: {
            required: true,
            message: 'İlgili banka zorunludur.',
        },
        paymentMethod: {
            required: true,
            message: 'Ödeme yöntemi zorunludur.',
        },
        receiptNumber: {
            required: true,
            message: 'Makbuz numarası zorunludur.',
        },
        description: {
            maxLength: 500,
            maxLengthMessage: 'Açıklama en fazla 500 karakter olabilir.',
        },
    }), []);

    const {
        formData: paymentData,
        errors,
        handleCancel: handleCrudCancel,
        handleInputChange,
        handleSubmit,
        isSubmitting,
    } = useModalCrudState({
        isOpen,
        mode,
        selectedItem: selectedPaymentCollection,
        initialData: initialPaymentData,
        toggle,
        mapSelectedToData: ({ initialData, selectedItem }) => {
            const formattedDate = toLocalISOString(selectedItem?.date || new Date());
            const bankId = Array.isArray(selectedItem?.associatedBank) && selectedItem.associatedBank.length > 0
                ? selectedItem.associatedBank[0]
                : selectedItem?.associatedBank?.id || '';

            const customerIds = autoAssignCustomer && customerId
                ? sanitizeIds([customerId])
                : sanitizeIds(selectedItem?.customer);

            const expenseIds = sanitizeIds(selectedItem?.expense);
            const accidentFileIds = sanitizeIds(selectedItem?.accidentFile);
            const callFormIds = sanitizeIds(selectedItem?.callForm);
            const enforcementIds = sanitizeIds(selectedItem?.enforcement);

            return {
                ...initialData,
                ...selectedItem,
                date: formattedDate,
                associatedBank: bankId,
                customer: customerIds,
                expense: expenseIds,
                accidentFile: accidentFileIds,
                callForm: callFormIds,
                enforcement: enforcementIds,
                amount: selectedItem?.amount !== undefined && selectedItem?.amount !== null
                    ? String(selectedItem.amount)
                    : '',
                receiptNumber: selectedItem?.receiptNumber !== undefined && selectedItem?.receiptNumber !== null
                    ? String(selectedItem.receiptNumber)
                    : '',
                paymentMethod: selectedItem?.paymentMethod || '',
            };
        },
        normalizeInput: true,
        validationSchema,
        submitConfig: {
            apiEndpoint,
            refreshData,
            componentName: 'Tahsilat',
            onSuccess: (result) => {
                if (mode === 'add' && onSaveSuccess && result) {
                    onSaveSuccess(result);
                }
                handleCancel();
            },
            buildPayload: (data) => {
                const amountToSend = parseCurrencyToFloat(data.amount);
                const receiptNumberToSend = data.receiptNumber ? String(data.receiptNumber).trim() : null;
                const dateToSend = data.date ? new Date(data.date).toISOString() : null;
                const bankIdToSend = data.associatedBank ? [String(data.associatedBank)] : [];

                const expenseToSend = sanitizeIds(data.expense);
                const paymentMethodToSend = data.paymentMethod === '' ? null : data.paymentMethod;
                const accidentFileToSend = sanitizeIds(data.accidentFile);
                const callFormToSend = sanitizeIds(data.callForm);
                const enforcementToSend = sanitizeIds(data.enforcement);

                return {
                    paymentCollectionNumber: data.paymentCollectionNumber || null,
                    paymentCollectionType: data.paymentCollectionType,
                    amount: amountToSend,
                    associatedBank: bankIdToSend,
                    date: dateToSend,
                    paymentMethod: paymentMethodToSend,
                    receiptNumber: receiptNumberToSend,
                    description: data.description || '',
                    expense: expenseToSend,
                    accidentFile: accidentFileToSend,
                    customer: Array.isArray(data.customer) ? data.customer.map(id => String(id)) : [],
                    callForm: callFormToSend,
                    enforcement: enforcementToSend,
                    ...(mode === 'modify' && selectedPaymentCollection?.id && { id: selectedPaymentCollection.id })
                };
            },
        },
    });

    const bankApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/bank';

    useEffect(() => {
        const fetchBanks = async () => {
            if (isOpen && bankApiUrl) {
                try {
                    await fetchTableData({
                        apiEndpoint: bankApiUrl,
                        page: 1,
                        pageSize: 1000,
                        setDataCallback: (apiResponse) => {
                            const items = apiResponse?.data?.items || apiResponse?.items || (Array.isArray(apiResponse) ? apiResponse : []);
                            setBanks(items);
                        },
                        setTotalRowsCallback: () => {}
                    });
                } catch (error) {
                    if (shouldToastError(error)) {
                        toast.error("Bankalar yüklenirken bir hata oluştu.");
                    }
                    setBanks([]);
                }
            }
        };
        fetchBanks();
    }, [isOpen, bankApiUrl]);

    useEffect(() => {
        if (isOpen) {
            if (mode === 'modify' && selectedPaymentCollection) {
                const associatedExpense = selectedPaymentCollection.expense;

                if (associatedExpense && typeof associatedExpense === 'object' && associatedExpense.id) {
                    setAssociatedExpenseId(associatedExpense.id);
                    setAssociatedExpenseDisplay(associatedExpense.expenseNumber || `ID: ${associatedExpense.id}`);
                    setAssociatedExpenseAmount(associatedExpense.amount ?? null);
                    setExpenseSearchNumber(associatedExpense.expenseNumber || '');
                } else {
                    setAssociatedExpenseDisplay('');
                    setAssociatedExpenseId(null);
                    setAssociatedExpenseAmount(null);
                    setExpenseSearchNumber('');
                }
            } else if (mode === 'add') {
                if (directExpenseAssociationId) {
                    setAssociatedExpenseId(directExpenseAssociationId);
                    setExpenseSearchNumber('');
                    setAssociatedExpenseDisplay('');
                    setAssociatedExpenseAmount(null);
                } else {
                    setExpenseSearchNumber('');
                    setAssociatedExpenseDisplay('');
                    setAssociatedExpenseId(null);
                    setAssociatedExpenseAmount(null);
                }
            }
        }
    }, [isOpen, mode, selectedPaymentCollection, directExpenseAssociationId]);

    const handleCancel = () => {
        setExpenseSearchNumber('');
        setAssociatedExpenseDisplay('');
        setAssociatedExpenseId(null);
        setAssociatedExpenseAmount(null);
        setIsSearchingExpense(false);
        handleCrudCancel();
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return '';
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value);
    }

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} backdrop="static">
            <ModalHeader toggle={handleCancel}>
                {mode === 'add' ? 'Tahsilat Ekle' : 'Tahsilat Düzenle'}
            </ModalHeader>
            <ModalBody>
                <Form>
                    <FormGroup>
                        <Label for="paymentCollectionNumber">Tahsilat Numarası</Label>
                        <Input
                            type="text"
                            id="paymentCollectionNumber"
                            name="paymentCollectionNumber"
                            value={paymentData.paymentCollectionNumber || ''}
                            readOnly
                            disabled
                            placeholder="Sistem Tarafından Atanır"
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="paymentCollectionType">Tahsilat Tipi <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="paymentCollectionType"
                            name="paymentCollectionType"
                            value={paymentData.paymentCollectionType || ''}
                            onChange={handleInputChange}
                            placeholder="Tahsilatın Türünü Girin (Örn: Müşteri Ödemesi)"
                            invalid={!!errors.paymentCollectionType}
                        />
                        <FormFeedback>{errors.paymentCollectionType}</FormFeedback>
                    </FormGroup>

                    <FormGroup>
                        <Label for="amount">Tutar <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="amount"
                            name="amount"
                            value={displayDecimal(paymentData.amount || '')}
                            onChange={handleInputChange}
                            placeholder="Örn: 1000,50"
                            invalid={!!errors.amount}
                        />
                        <FormFeedback>{errors.amount}</FormFeedback>
                    </FormGroup>

                    <FormGroup>
                        <Label for="associatedBank">İlgili Banka <span className="text-danger">*</span></Label>
                        <Input
                            type="select"
                            id="associatedBank"
                            name="associatedBank"
                            value={paymentData.associatedBank || ''}
                            onChange={handleInputChange}
                            invalid={!!errors.associatedBank}
                        >
                            <option value="">Banka Seçiniz</option>
                            {banks?.filter(bank => !bank.code).map(bank => (
                                <option key={bank.id} value={String(bank.id)}>
                                    {bank.bankName} {bank.branchName ? `- ${bank.branchName}` : ''} (IBAN: {bank.iban})
                                </option>
                            ))}
                        </Input>
                        <FormFeedback>{errors.associatedBank}</FormFeedback>
                    </FormGroup>

                    <FormGroup>
                        <Label for="date">Tarih ve Saat <span className="text-danger">*</span></Label>
                        <Input
                            type="datetime-local"
                            id="date"
                            name="date"
                            value={paymentData.date || ''}
                            onChange={handleInputChange}
                            invalid={!!errors.date}
                        />
                        <FormFeedback>{errors.date}</FormFeedback>
                    </FormGroup>

                    <FormGroup>
                        <Label for="paymentMethod">Ödeme Yöntemi <span className="text-danger">*</span></Label>
                        <Input
                            type="select"
                            id="paymentMethod"
                            name="paymentMethod"
                            value={paymentData.paymentMethod || ''}
                            onChange={handleInputChange}
                            invalid={!!errors.paymentMethod}
                        >
                            <option value="">Ödeme Yöntemi Seçiniz</option>
                            {PAYMENT_METHODS.map(method => (
                                <option key={method} value={method}>{method}</option>
                            ))}
                        </Input>
                        <FormFeedback>{errors.paymentMethod}</FormFeedback>
                    </FormGroup>

                    <FormGroup>
                        <Label for="receiptNumber">Makbuz Numarası <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="receiptNumber"
                            name="receiptNumber"
                            value={paymentData.receiptNumber || ''}
                            onChange={handleInputChange}
                            placeholder="Makbuz Numarası"
                            invalid={!!errors.receiptNumber}
                        />
                        <FormFeedback>{errors.receiptNumber}</FormFeedback>
                    </FormGroup>

                    {(mode === 'modify' || directExpenseAssociationId) && associatedExpenseDisplay && (
                         <FormGroup>
                            <Label>İlişkili Masraf</Label>
                            <div className="p-2 border rounded" style={{ backgroundColor: '#e9ecef', fontSize: '0.875rem' }}>
                                <div>Masraf Numarası: <strong>{associatedExpenseDisplay}</strong></div>
                                {associatedExpenseAmount !== null && (
                                    <div>Tutar: <strong>{formatCurrency(associatedExpenseAmount)}</strong></div>
                                )}
                            </div>
                        </FormGroup>
                    )}
                    {mode === 'add' && directExpenseAssociationId && !associatedExpenseDisplay && (
                        <FormGroup>
                            <Label>İlişkili Masraf</Label>
                            <div className="p-2 border rounded" style={{ backgroundColor: '#e9ecef', fontSize: '0.875rem' }}>
                                Bu tahsilat bir masrafa bağlanacaktır.
                            </div>
                        </FormGroup>
                    )}

                    <FormGroup>
                        <Label for="description">Açıklama</Label>
                        <Input
                            type="textarea"
                            id="description"
                            name="description"
                            value={paymentData.description || ''}
                            onChange={handleInputChange}
                            placeholder="Tahsilatla İlgili Ek Açıklamalar (Opsiyonel)"
                            rows="3"
                            invalid={!!errors.description}
                        />
                        <FormFeedback>{errors.description}</FormFeedback>
                    </FormGroup>
                </Form>

                <hr className="my-4" />
                <h6 className="mb-3">Fatura</h6>
                {mode === 'modify' && selectedPaymentCollection?.id ? (
                    <FilesComponent
                        referenceId={selectedPaymentCollection.id}
                        callFormReference={false}
                        multiple={false}
                        canEdit={true}
                        typeFilter={["Fatura"]}
                        title=""
                        onFilesChange={() => { }}
                    />
                ) : (
                    <div className="text-muted small">Fatura eklemek için önce tahsilatı kaydedin.</div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="outline-success" onClick={handleSubmit} disabled={isSubmitting} className="rounded-pill">
                    {isSubmitting ? 'Kaydediliyor...' : (mode === 'add' ? 'Ekle' : 'Güncelle')}
                </Button>
                <Button color="outline-danger" onClick={handleCancel} disabled={isSubmitting} className="ms-2 rounded-pill">
                    İptal
                </Button>
            </ModalFooter>
        </Modal>
    );
};

PaymentCollectionDataModal.propTypes = {
    apiEndpoint: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['add', 'modify']).isRequired,
    selectedPaymentCollection: PropTypes.object,
    refreshData: PropTypes.func.isRequired,
    customerId: PropTypes.string,
    autoAssignCustomer: PropTypes.bool,
    onSaveSuccess: PropTypes.func,
    directExpenseAssociationId: PropTypes.string,
    accidentFile: PropTypes.arrayOf(PropTypes.string),
    callForm: PropTypes.arrayOf(PropTypes.string),
    enforcement: PropTypes.arrayOf(PropTypes.string)
};

PaymentCollectionDataModal.defaultProps = {
    onSaveSuccess: null,
    directExpenseAssociationId: null,
    accidentFile: [],
    callForm: [],
    enforcement: []
};

export default PaymentCollectionDataModal;
